import React, {useState, useEffect, useRef, KeyboardEvent, ChangeEvent} from "react";
import {useNavigate} from "react-router-dom";
import {searchMulti} from "../api";
import {NoPoster} from "./NoPoster";
import {ROUTES} from "../constants/routes";

interface SearchResult {
  id: number;
  media_type: "movie" | "tv";
  title?: string;
  name?: string;
  poster_path?: string | null;
  release_date?: string;
  first_air_date?: string;
}

export const SearchInput: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await searchMulti(query);
        const filtered = (data.results as SearchResult[])
          .filter((r) => r.media_type === "movie" || r.media_type === "tv")
          .slice(0, 8);
        setResults(filtered);
        setIsOpen(true);
        setActiveIndex(-1);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navigateTo = (item: SearchResult) => {
    const title = item.title || item.name || "";
    navigate(ROUTES.content(item.media_type, item.id, title));
    setIsOpen(false);
    setQuery("");
  };

  const handleSubmit = () => {
    if (!query.trim()) return;
    if (activeIndex >= 0 && results[activeIndex]) {
      navigateTo(results[activeIndex]);
      return;
    }
    navigate(ROUTES.search(query));
    setIsOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit();
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    }
  };

  return (
    <div ref={containerRef} className="relative flex items-center gap-1.5 w-full">
      <div className="relative flex-1 min-w-0">
        <input
          type="text"
          placeholder="Search movies, shows..."
          className="w-full h-9 pl-9 pr-3.5 rounded-sm border border-border bg-raised text-t1 font-[inherit] text-sm placeholder-t3 transition-colors duration-fast focus:outline-none focus:border-accent"
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
        />
        {/* Search icon inside input */}
        <svg
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-t3 pointer-events-none"
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
        </svg>

        {/* Loading spinner */}
        {isLoading && (
          <svg
            className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-t3"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
            <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        )}
      </div>

      <button
        className="flex items-center justify-center h-9 w-9 rounded-sm border-none bg-accent text-white flex-shrink-0 transition-all duration-fast hover:opacity-90 active:scale-95"
        onClick={handleSubmit}
        aria-label="Search"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="white">
          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
        </svg>
      </button>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-[calc(100%+6px)] left-0 right-0 min-w-[300px] bg-raised border border-border2 rounded-md shadow-lg z-[400] overflow-hidden">
          {results.map((item, idx) => {
            const label = item.title || item.name || "";
            const year = item.release_date
              ? item.release_date.slice(0, 4)
              : item.first_air_date
              ? item.first_air_date.slice(0, 4)
              : null;
            return (
              <button
                key={item.id}
                onMouseDown={() => navigateTo(item)}
                className={`w-full flex items-center gap-3 px-3 py-2 border-none bg-transparent text-left cursor-pointer transition-colors duration-fast ${
                  idx === activeIndex ? "bg-raised2" : "hover:bg-raised2"
                }`}
              >
                <div className="w-8 h-12 rounded-xs flex-shrink-0 overflow-hidden bg-raised2">
                  {item.poster_path ? (
                    <img
                      src={`https://image.tmdb.org/t/p/w92${item.poster_path}`}
                      alt={label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <NoPoster />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-t1 truncate">{label}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-[0.4px] px-1.5 py-0.5 rounded-xs ${
                        item.media_type === "movie"
                          ? "bg-accent-muted text-accent"
                          : "bg-tv-blue-muted text-tv-blue"
                      }`}
                    >
                      {item.media_type === "movie" ? "Movie" : "TV"}
                    </span>
                    {year && <span className="text-[11px] text-t3">{year}</span>}
                  </div>
                </div>
              </button>
            );
          })}

          <button
            onMouseDown={handleSubmit}
            className="w-full px-3 py-2.5 border-none border-t border-border bg-transparent text-[12px] font-medium text-t3 hover:text-accent hover:bg-raised2 transition-colors duration-fast cursor-pointer text-center"
          >
            See all results for "{query}"
          </button>
        </div>
      )}
    </div>
  );
};
